/**
 * ScenarioLab - Passenger Decision Testing Laboratory
 * 
 * Provides a configurable testing environment for the Passenger Decision
 * governance model, allowing users to test various disruption scenarios
 * and see how the system processes advisories.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Play,
  GitCompare,
  FileText,
  Loader2
} from 'lucide-react';
import CompareRuns from './CompareRuns';
import ReceiptViewer from './ReceiptViewer';
import { QuickPresets, ConfigurationPanel } from './ScenarioLab/config';
import { ScenarioResults } from './ScenarioLab/results';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default configuration for scenarios
const DEFAULT_CONFIG = {
  passenger_name: "Test Passenger",
  mobility_requirement: "reduced_mobility",
  requires_lift_access: true,
  requires_staff_assistance: true,
  appointment_time: "14:30",
  appointment_urgency: "critical",
  disruption_types: ["service_delay", "lift_outage"],
  disruption_severity: "medium",
  include_verified_operator: true,
  include_verified_network: true,
  include_station_staff: true,
  include_unverified_source: true,
  unverified_advisory_type: "proceed_unsafe",
  test_run_id: ""
};

const ScenarioLab = () => {
  const [activeSubTab, setActiveSubTab] = useState('run');
  const [parameters, setParameters] = useState(null);
  const [presets, setPresets] = useState(null);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState(null);
  
  // Load parameters and presets on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [paramsRes, presetsRes] = await Promise.all([
          fetch(`${API_URL}/api/passenger/lab/parameters`),
          fetch(`${API_URL}/api/passenger/lab/presets`)
        ]);
        const paramsData = await paramsRes.json();
        const presetsData = await presetsRes.json();
        setParameters(paramsData);
        setPresets(presetsData.presets);
        setError(null);
      } catch (err) {
        setError('Failed to load lab data');
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - load once on mount
  
  const runScenario = useCallback(async () => {
    setLoading(true);
    setResult(null);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/passenger/lab/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      
      if (!response.ok) throw new Error('Scenario run failed');
      
      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError('Scenario run failed');
    } finally {
      setLoading(false);
    }
  }, [config]);
  
  const applyPreset = (preset) => {
    setConfig(prev => ({
      ...prev,
      ...preset.config,
      test_run_id: ""
    }));
    setResult(null);
  };
  
  const resetConfig = () => {
    if (parameters?.default_config) {
      setConfig({
        ...parameters.default_config,
        passenger_name: "Test Passenger",
        test_run_id: ""
      });
    }
    setResult(null);
  };
  
  const copyConfig = () => {
    navigator.clipboard.writeText(JSON.stringify(config, null, 2));
  };
  
  if (!parameters) {
    return (
      <div className="flex items-center justify-center h-64">
        {error ? (
          <div className="text-red-400">{error}</div>
        ) : (
          <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
        )}
      </div>
    );
  }
  
  return (
    <div data-testid="scenario-lab" className="space-y-6">
      {/* Error display */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
          {error}
        </div>
      )}
      
      {/* Sub-tab switcher */}
      <div className="flex gap-2 border-b border-slate-700 pb-3">
        <button
          data-testid="subtab-run"
          onClick={() => setActiveSubTab('run')}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors flex items-center gap-2 ${
            activeSubTab === 'run'
              ? 'bg-slate-800 text-white border-b-2 border-cyan-500'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <Play className="w-4 h-4" />
          Run Scenario
        </button>
        <button
          data-testid="subtab-compare"
          onClick={() => setActiveSubTab('compare')}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors flex items-center gap-2 ${
            activeSubTab === 'compare'
              ? 'bg-slate-800 text-white border-b-2 border-cyan-500'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <GitCompare className="w-4 h-4" />
          Compare Runs
        </button>
        <button
          data-testid="subtab-receipts"
          onClick={() => setActiveSubTab('receipts')}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors flex items-center gap-2 ${
            activeSubTab === 'receipts'
              ? 'bg-slate-800 text-white border-b-2 border-cyan-500'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <FileText className="w-4 h-4" />
          Receipt History
        </button>
      </div>

      {/* Compare Runs Tab */}
      {activeSubTab === 'compare' && <CompareRuns />}

      {/* Receipt History Tab */}
      {activeSubTab === 'receipts' && <ReceiptViewer />}

      {/* Run Scenario Tab */}
      {activeSubTab === 'run' && (
        <>
          <QuickPresets presets={presets} onApply={applyPreset} />
          
          <ConfigurationPanel
            config={config}
            setConfig={setConfig}
            parameters={parameters}
            showAdvanced={showAdvanced}
            setShowAdvanced={setShowAdvanced}
            onRun={runScenario}
            onReset={resetConfig}
            onCopy={copyConfig}
            loading={loading}
          />
          
          <ScenarioResults result={result} />
        </>
      )}
    </div>
  );
};

export default ScenarioLab;
